import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { stringify } from 'qs';

import configs from '@webtrader/core/configs';
import { EProjectPlatform } from '@webtrader/core/configs/types';
import { IApiRequest, IApiRequestWithPayload } from '@webtrader/core/request/api';
import { defaultErrorHandler } from '@webtrader/core/request/handler';
import { exportService } from '@webtrader/core/services/export';
import { maintenanceService } from '@webtrader/core/services/maintenance';
import storage, { EStorageKey } from '@webtrader/core/storage';

const isNative = configs.platform === EProjectPlatform.NATIVE;

const axiosInstance = axios.create({
  baseURL: configs.baseURL,
  headers: {
    'Content-Type': 'application/json'
    // SK: Comment to codes for cross origin
    // 'X-XSS-Protection': '0',
    // 'Referrer-Policy': 'no-referrer',
    // 'X-Permitted-Cross-Domain-Policies': 'none'
  },
  paramsSerializer: (params) => stringify(params, { arrayFormat: 'comma' })
});

axiosInstance.interceptors.request.use((configs) => {
  const url = configs.url || '';
  const token = storage.getItem<string>(EStorageKey.TOKEN);
  const language = storage.getItem(EStorageKey.LANGUAGE);

  const axiosBaseConfigs: AxiosRequestConfig = {
    ...configs
  };

  if (token) {
    if (!['/api/auth/v1/company/login'].includes(url)) {
      axiosBaseConfigs.headers = {
        ...axiosBaseConfigs.headers,
        authorization: `Bearer ${token}`
      };
    } else if (['/api/support/v1/ticket'].includes(url)) {
      axiosBaseConfigs.headers = {
        ...axiosBaseConfigs.headers,
        authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'multipart/form-data'
      };
    }
  }

  if (isNative) {
    if (!axiosBaseConfigs.headers) {
      axiosBaseConfigs.headers = {};
    }
    axiosBaseConfigs.headers['cookie'] = `lang=${language}`;
  }

  return axiosBaseConfigs;
});

axiosInstance.interceptors.response.use(
  (response) => {
    maintenanceService.checkForMaintenance(response);

    exportService.checkForDownload(response);

    return response.data;
  },
  (error) => {
    const { config } = error;

    if (!config?.retry) {
      return defaultErrorHandler(error);
    }
    config.retry = config.retry - 1;
    const delayRetryRequest = new Promise<void>((resolve) => {
      setTimeout(() => {
        // eslint-disable-next-line no-console
        console.debug('🚧 Retrying request: ', config.url);
        resolve();
      }, config.retryDelay || 1000);
    });
    return delayRetryRequest.then(() => axiosInstance(config));
  }
);

axiosInstance.defaults.withCredentials = true;

class ApiRequest {
  static get: IApiRequest = axiosInstance.get;
  static head: IApiRequest = axiosInstance.head;
  static delete: IApiRequest = axiosInstance.delete;
  static put: IApiRequestWithPayload = axiosInstance.put;
  static post: IApiRequestWithPayload = axiosInstance.post;
  static patch: IApiRequestWithPayload = axiosInstance.patch;
}

export default ApiRequest;

export { axios, axiosInstance, ApiRequest };

export type { AxiosError, AxiosRequestConfig, AxiosResponse };
